import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Database, DatabaseBackup, RecoveryWindow, Service } from "@/lib/types";
import { Panel } from "@/ui/Panel";
import { Head } from "@/ui/frame/Page";
import { Button } from "@/ui/Button";
import { Chip, type ChipTone } from "@/ui/Chip";
import { StateLine } from "@/ui/Lamp";
import { Table, Td, Th, Tr } from "@/ui/Table";
import { CopyField } from "@/ui/Copy";
import { Field, Input } from "@/ui/Field";
import { EmptyState, ErrorState, Skeleton, TableSkeleton } from "@/ui/States";
import { useToast } from "@/ui/Toast";
import { absoluteTime, bytes, relativeTime } from "@/lib/format";

export const Route = createFileRoute(
  "/_app/o/$orgId/projects/$projectId/databases/$databaseId",
)({
  component: DatabaseScreen,
});

const BACKUP_STATUS: Record<
  DatabaseBackup["status"],
  { label: string; tone: ChipTone | undefined }
> = {
  running: { label: "In progress", tone: "live" },
  completed: { label: "Complete", tone: undefined },
  failed: { label: "Failed", tone: "down" },
};

function DatabaseScreen() {
  const { orgId, projectId, databaseId } = Route.useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const [connection, setConnection] = useState<string | null>(null);

  const base = `/organisations/${orgId}/projects/${projectId}/databases/${databaseId}`;

  const database = useQuery({
    queryKey: ["database", databaseId],
    queryFn: ({ signal }) => api.get<{ database: Database }>(base, undefined, signal),
    refetchInterval: (query) => {
      const state = query.state.data?.database.state;
      return state && ["QUEUED", "DEPLOYING", "STARTING", "DELETING"].includes(state)
        ? 4000
        : false;
    },
  });

  const backups = useQuery({
    queryKey: ["database-backups", databaseId],
    queryFn: ({ signal }) =>
      api.get<{ backups: DatabaseBackup[] | null }>(`${base}/backups`, undefined, signal),
  });

  const services = useQuery({
    queryKey: ["services", orgId, projectId],
    queryFn: ({ signal }) =>
      api.get<{ services: Service[] | null }>(
        `/organisations/${orgId}/projects/${projectId}/services`,
        undefined,
        signal,
      ),
  });

  const access = useQuery({
    queryKey: ["database-access", databaseId],
    queryFn: ({ signal }) =>
      api.get<{ service_ids: string[] | null }>(`${base}/access`, undefined, signal),
  });

  const recovery = useQuery({
    queryKey: ["database-recovery-window", databaseId],
    queryFn: ({ signal }) =>
      api.get<{ window: RecoveryWindow }>(`${base}/recovery-window`, undefined, signal),
  });

  const restore = useMutation({
    mutationFn: (pointInTime: string | null) =>
      api.post<{ database: Database }>(`${base}/restore`,
        pointInTime ? { point_in_time: new Date(pointInTime).toISOString() } : {}),
    onSuccess: async ({ database: copy }) => {
      toast.success(
        `${copy.display_name} is being recovered`,
        "Your original database is untouched and still running.",
      );
      await queryClient.invalidateQueries({ queryKey: ["databases", orgId, projectId] });
      await navigate({
        to: "/o/$orgId/projects/$projectId/databases/$databaseId",
        params: { orgId, projectId, databaseId: copy.id },
      });
    },
    onError: toast.error,
  });

  const reveal = useMutation({
    mutationFn: () => api.post<{ credentials: { url: string } }>(`${base}/credentials`),
    onSuccess: ({ credentials }) => setConnection(credentials.url),
    onError: toast.error,
  });

  const setAccess = useMutation({
    mutationFn: ({ service, granted }: { service: Service; granted: boolean }) =>
      granted
        ? api.delete<void>(`${base}/access/${service.id}`)
        : api.put<void>(`${base}/access/${service.id}`),
    onSuccess: async (_result, { service, granted }) => {
      toast.success(
        granted
          ? `${service.display_name} can no longer reach this database`
          : `${service.display_name} can now reach this database`,
      );
      await queryClient.invalidateQueries({ queryKey: ["database-access", databaseId] });
    },
    onError: toast.error,
  });

  const remove = useMutation({
    mutationFn: () => api.delete<void>(base),
    onSuccess: async () => {
      toast.success("Database deleted", "Its archive is kept for 14 days, then removed.");
      await queryClient.invalidateQueries({ queryKey: ["databases", orgId, projectId] });
      await navigate({ to: "/o/$orgId/projects/$projectId/databases", params: { orgId, projectId } });
    },
    onError: toast.error,
  });

  if (database.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-7 w-56" />
        <Skeleton className="h-40 w-full rounded-xl" />
      </div>
    );
  }
  if (database.isError) {
    return <ErrorState error={database.error} onRetry={() => void database.refetch()} />;
  }

  const db = database.data!.database;
  const granted = new Set(access.data?.service_ids ?? []);
  const eligible = (services.data?.services ?? []).filter(
    (s) => s.environment_id === db.environment_id,
  );

  return (
    <div className="flex flex-col gap-6">
      <Head
        title={
          <span className="flex flex-wrap items-center gap-3">
            {db.display_name}
            <StateLine state={db.state} />
          </span>
        }
        description={db.state_reason || `PostgreSQL ${db.major_version}`}
        actions={
          <Link to="/o/$orgId/projects/$projectId/databases" params={{ orgId, projectId }}>
            <Button tone="ghost">All databases</Button>
          </Link>
        }
      />

      <Panel
        title="Connecting to it"
        description="The connection details are a credential. Anyone holding them can read and write everything in this database."
      >
        {connection ? (
          <CopyField label="Connection string" value={connection} multiline />
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-[13px] text-[var(--text-secondary)]">
              Showing the connection details is recorded in your organisation's activity log.
            </p>
            <Button
              tone="secondary"
              size="dense"
              busy={reveal.isPending}
              disabled={db.state !== "RUNNING"}
              onClick={() => reveal.mutate()}
            >
              Show connection details
            </Button>
          </div>
        )}
      </Panel>

      <Panel
        title="Which services can reach it"
        description="Nothing can connect until you say so. Being in the same environment is not enough."
        padded={false}
      >
        {services.isLoading || access.isLoading ? (
          <div className="px-5 py-4">
            <TableSkeleton rows={2} columns={2} />
          </div>
        ) : eligible.length === 0 ? (
          <div className="px-5 py-4">
            <EmptyState
              title="No services here yet"
              description="Services in this environment will appear here once you create them."
            />
          </div>
        ) : (
          <div className="px-5 py-4">
            <Table>
              <thead>
                <Tr>
                  <Th>Service</Th>
                  <Th>Access</Th>
                  <Th />
                </Tr>
              </thead>
              <tbody>
                {eligible.map((service) => {
                  const has = granted.has(service.id);
                  return (
                    <Tr key={service.id}>
                      <Td>
                        <span className="font-medium text-[var(--text-primary)]">
                          {service.display_name}
                        </span>
                      </Td>
                      <Td>
                        {has ? (
                          <Chip tone="live">Granted</Chip>
                        ) : (
                          <Chip>No access</Chip>
                        )}
                      </Td>
                      <Td align="right">
                        <Button
                          tone="ghost"
                          size="dense"
                          busy={
                            setAccess.isPending && setAccess.variables?.service.id === service.id
                          }
                          onClick={() => setAccess.mutate({ service, granted: has })}
                        >
                          {has ? "Revoke" : "Grant access"}
                        </Button>
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </Panel>

      <Panel
        title="Backups"
        description={
          db.backups.enabled
            ? `Taken automatically and kept for ${db.backups.retention_days} days.`
            : "Backups are switched off for this database."
        }
        padded={false}
      >
        {backups.isLoading ? (
          <div className="px-5 py-4">
            <TableSkeleton rows={3} columns={4} />
          </div>
        ) : (backups.data?.backups ?? []).length === 0 ? (
          <div className="px-5 py-4">
            <EmptyState
              title="No backups yet"
              description="The first backup is taken shortly after the database starts."
            />
          </div>
        ) : (
          <div className="px-5 py-4">
            <Table>
              <thead>
                <Tr>
                  <Th>Taken</Th>
                  <Th>Kind</Th>
                  <Th>Status</Th>
                  <Th align="right">Size</Th>
                </Tr>
              </thead>
              <tbody>
                {(backups.data?.backups ?? []).map((backup) => (
                  <Tr key={backup.id}>
                    <Td title={absoluteTime(backup.started_at)}>
                      {relativeTime(backup.started_at)}
                    </Td>
                    <Td>{backup.kind === "manual" ? "By hand" : "Scheduled"}</Td>
                    <Td>
                      <Chip tone={BACKUP_STATUS[backup.status].tone}>
                        {BACKUP_STATUS[backup.status].label}
                      </Chip>
                      {backup.reason && (
                        <p className="mt-1 text-[13px] text-[var(--text-secondary)]">
                          {backup.reason}
                        </p>
                      )}
                    </Td>
                    <Td align="right">
                      {backup.size_bytes ? bytes(backup.size_bytes) : "—"}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Panel>

      <RecoveryPanel
        database={db}
        window={recovery.data?.window}
        loading={recovery.isLoading}
        restoring={restore.isPending}
        onRestore={(at) => restore.mutate(at)}
      />

      <Panel
        tone="down"
      title="Delete this database"
        description="The database is removed. Its archive is kept for 14 days, so a deletion made by mistake can still be undone. After that it is deleted too."
      >
        <Button tone="danger" size="dense" busy={remove.isPending} onClick={() => remove.mutate()}>
          Delete database
        </Button>
      </Panel>
    </div>
  );
}

/**
 * Recovering to a moment in the past.
 *
 * The wording matters more than usual here. Somebody reading this screen has
 * probably just done something they regret, and the single most important thing
 * to tell them is that recovering does not touch what they already have.
 */
function RecoveryPanel({
  database,
  window,
  loading,
  restoring,
  onRestore,
}: {
  database: Database;
  window?: RecoveryWindow;
  loading: boolean;
  restoring: boolean;
  onRestore: (pointInTime: string | null) => void;
}) {
  const [moment, setMoment] = useState("");

  if (database.restored_from) {
    return (
      <Panel title="Where this came from">
        <p className="text-[13px] text-[var(--text-secondary)]">
          This database was recovered from another one
          {database.restored_to ? `, as it was at ${absoluteTime(database.restored_to)}` : ""}.
          Connect a service to it when you are ready, and delete whichever copy you no
          longer want.
        </p>
      </Panel>
    );
  }

  const available = window?.available === true;

  return (
    <Panel
      title="Recover to a moment in the past"
      description="Creates a new database from this one's backups. This database is not touched and keeps running. Connect your service to the copy when you are happy with it, and delete whichever one you no longer want."
    >
      {loading ? (
        <Skeleton className="h-16 w-full rounded-lg" />
      ) : !available ? (
        <p className="text-[13px] text-[var(--text-secondary)]">
          {window?.unavailable ??
            "There is nothing to recover from yet. The first backup is taken shortly after a database starts."}
        </p>
      ) : (
        <div className="flex flex-wrap items-end gap-3">
          <Field
            label="Moment"
            hint={
              window?.earliest
                ? `Anything from ${absoluteTime(window.earliest)} onwards. Leave empty for the most recent moment.`
                : "Leave empty for the most recent moment."
            }
          >
            {({ id }) => (
              <Input
                id={id}
                type="datetime-local"
                value={moment}
                min={window?.earliest ? toLocalInput(window.earliest) : undefined}
                max={window?.latest ? toLocalInput(window.latest) : undefined}
                onChange={(e) => setMoment(e.target.value)}
              />
            )}
          </Field>
          <Button
            tone="secondary"
            size="dense"
            busy={restoring}
            onClick={() => onRestore(moment || null)}
          >
            Recover
          </Button>
        </div>
      )}
    </Panel>
  );
}

/**
 * A datetime-local input wants the viewer's own clock, without a zone.
 *
 * Slicing the ISO string would silently show a UTC time as if it were local,
 * which for anyone outside UTC means the bounds on this field are wrong by
 * their offset — in the one place where picking the wrong moment matters.
 */
function toLocalInput(iso: string): string {
  const date = new Date(iso);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}
